import type { Copy } from "../types";

export const ru: Copy = {
    seo: {
      title: "Калькулятор возраста автомобиля | проверка по году и месяцу",
      description:
        "Проверьте точный возраст автомобиля по году выпуска и необязательному месяцу. Видите годы, месяцы, дни, общее число дней и вехи в одном удобном инструменте.",
      keywords: [
        "калькулятор возраста автомобиля",
        "возраст авто",
        "возраст автомобиля по году",
        "год выпуска автомобиля",
        "месяц выпуска автомобиля",
        "сколько лет моей машине",
        "проверка возраста автомобиля",
        "калькулятор возраста RC",
        "калькулятор даты автомобиля",
        "возраст авто по месяцу",
        "альтернатива mParivahan",
        "альтернатива Parivahan",
        "калькулятор возраста автомобиля RTO",
        "проверка возраста регистрации авто",
        "возраст регистрации автомобиля",
      ],
      faq: [
        {
          question: "Что делает этот калькулятор?",
          answer:
            "Он вычисляет точный возраст автомобиля по выбранному году выпуска и необязательному месяцу, а затем показывает годы, месяцы, дни и вехи.",
        },
        {
          question: "Зачем нужен необязательный месяц?",
          answer:
            "Месяц делает расчёт более точным. Если вы его не укажете, приложение использует январь выбранного года.",
        },
        {
          question: "Можно ли выбрать будущий год или месяц?",
          answer:
            "Нет. Будущие годы заблокированы, а будущие месяцы отключаются, если выбранный год совпадает с текущим.",
        },
      ],
    },
    headerTagline: "Инструмент для авто",
    headerTitle: "Калькулятор возраста автомобиля",
    reset: "Сбросить",
    switchToLight: "Светлая тема",
    switchToDark: "Тёмная тема",
    languageLabel: "Язык",
    languageEnglish: "Английский",
    languageHindi: "Хинди",
    heroEyebrow: "Возраст авто, понятно",
    heroTitle: "Чистый инструмент для определения возраста автомобиля.",
    heroDescription:
      "Выберите год выпуска. Добавляйте месяц только если хотите более точный результат.",
    yearSelectLabel: "Год выпуска",
    monthSelectLabel: "Месяц выпуска",
    includeMonthLabel: "Добавить конкретный месяц для большей точности",
    includeMonthNote:
      "Если месяц не указан, для расчёта используется январь.",
    yearOnlyBadge: "Только год",
    yearMonthBadge: "Год + месяц",
    yearPlaceholder: "Выберите год",
    monthPlaceholder: "Выберите месяц",
    inputTitle: "Год выпуска",
    inputLabel: "Год выпуска",
    blockedDateNote: "Будущие годы заблокированы.",
    result: "Результат",
    vehicleAgeLead: "Ваш автомобиль",
    asOfToday: "На сегодня",
    years: "Годы",
    months: "Месяцы",
    days: "Дни",
    totalDays: (count) => `${count.toLocaleString("en-IN")} дней всего`,
    ageLabels: {
      almostNew: "Почти новый",
      modern: "Современный",
      mature: "Зрелый",
      classic: "Классический",
    },
    estimatedValue: {
      high: "Высокий потенциал перепродажи",
      healthy: "Хорошее удержание стоимости",
      moderate: "Умеренная амортизация",
      collector: "Интерес коллекционеров может вырасти",
    },
    maintenanceTitle: "Обслуживание",
    maintenance: {
      routine: "Регулярное обслуживание и уход OEM",
      watch: "Следите за тормозами, жидкостями и шинами",
      plan: "Планируйте расходники и проверки",
      specialized: "Специализированное обслуживание становится важнее",
    },
    registrationTitle: "Регистрация",
    registration: {
      collector: "В некоторых регионах может применяться статус коллекционного авто",
      classic: "В некоторых регионах может применяться классическая регистрация",
      standard: "Ожидайте обычные продления и региональные проверки",
      regular: "Обычно применяется обычная регистрация",
    },
    upcomingMilestoneTitle: "Следующая веха",
    milestoneTimelineTitle: "Хронология вех",
    milestoneRemaining: ({ label, days }) => `${label} через ${days.toLocaleString("ru-RU")} дн.`,
    reached: "Достигнуто",
    upcoming: "Скоро",
    allMilestonesReachedTitle: "Все перечисленные вехи уже достигнуты",
    allMilestonesReachedBody:
      "Этот автомобиль уже вышел за пределы диапазона вех, показанного в приложении.",
    copySummary: "Скопировать сводку",
    shareResult: "Поделиться результатом",
    resetMessage: "Калькулятор сброшен",
    summaryCopied: "Сводка скопирована в буфер обмена",
    clipboardFailed: "Не удалось получить доступ к буферу обмена",
    emptyTitle: "Выберите год выпуска, чтобы увидеть полную сводку",
    emptyDescription:
      "После выбора года вы увидите точный возраст, общее число дней, заметки по обслуживанию, классический статус и вехи.",
    shareTitle: "Калькулятор возраста автомобиля",
    summary: ({ label, years, months, days, asOf }) =>
      `Мой автомобиль был произведён в ${label}, и на ${asOf} ему ${years} лет, ${months} месяцев и ${days} дней.`,
    milestones: {
      1: { label: "Первый год", note: "Полезно для гарантии и планирования сервиса." },
      3: { label: "Три года", note: "Частая точка для перепродажи и изменения стоимости." },
      5: { label: "Пять лет", note: "Хорошее время для пересмотра бюджета на обслуживание." },
      10: { label: "Десять лет", note: "Стареющие детали требуют большего внимания." },
      15: { label: "Пятнадцать лет", note: "Во многих регионах здесь начинается классический режим." },
      25: { label: "Двадцать пять лет", note: "Может быть интересен коллекционерам и импорту." },
    },
  };
